import { PageHref, Routes } from '@/constants';
import { LOGIN_HINT_PARAM, PROTECTED_FEATURE_PAGES, PUBLIC_PAGES } from '@/constants/pages';
import { CurrentUserModel } from '@/core/models/CurrentUserModel';
import { getRedirectUrl } from '@/core/utils/url';

export const isProtectedFeaturePage = (page: string): boolean => {
  return PROTECTED_FEATURE_PAGES.some((pattern) => new RegExp(pattern).test(page));
};

export const isPublicPage = (page: PageHref, searchParams: URLSearchParams): boolean => {
  const loginHint = searchParams.get(LOGIN_HINT_PARAM);
  return !!loginHint || PUBLIC_PAGES.includes(page);
};

export const getPagesByUserPermissions = ({ user }: { user: CurrentUserModel }): Record<PageHref, boolean> => {
  const pagePermissions = Object.values(Routes).reduce(
    (acc, route) => {
      acc[route] = user.isPageAvailable({ page: route });
      return acc;
    },
    {} as Record<PageHref, boolean>,
  );

  return pagePermissions;
};

export const getAllowedPageToRedirect = ({
  pagePermissions,
  user,
}: {
  pagePermissions: ReturnType<typeof getPagesByUserPermissions>;
  user: CurrentUserModel;
}) => {
  const { user_id: userId, org_id: orgId } = user;
  const hasProfile = !!(user.first_name && user.last_name);
  const allowedPages = (Object.keys(pagePermissions) as PageHref[]).filter((key) => !!(pagePermissions as any)[key]);
  let firstAllowedPage = allowedPages[0];

  if (userId && orgId) {
    const featurePages = allowedPages.filter((page) => isProtectedFeaturePage(page));
    if (featurePages.length > 0) {
      firstAllowedPage = featurePages[0];
    }
    if (user.hasPaymentAccess() && !user.isActivePaymentSubscription()) {
      firstAllowedPage = Routes.SETTINGS_SUBSCRIPTION;
    }
  } else if (userId && hasProfile && !orgId) {
    firstAllowedPage = Routes.ORGANIZATIONS;
  } else if (userId && !hasProfile) {
    firstAllowedPage = Routes.REGISTRATION_COMPLETE;
  }

  const redirectUrl = getRedirectUrl(firstAllowedPage) as PageHref;
  const isRedirectUrlAllowed = !!allowedPages.filter((page) => redirectUrl?.startsWith(page))[0];
  const redirectPage = isRedirectUrlAllowed ? redirectUrl : firstAllowedPage;

  return {
    redirectPage,
  };
};
